import React, { useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { User as UserIcon, Mail, Lock, Save, Shield, Languages } from 'lucide-react';

export default function ProfilePage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [info, setInfo] = useState({ full_name: user?.full_name || '', email: user?.email || '' });
  const [pwd, setPwd] = useState({ current_password: '', new_password: '', confirm: '' });
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const saveInfo = async (e) => {
    e.preventDefault();
    setSavingInfo(true);
    try {
      const { data } = await api.put('/auth/me', info);
      localStorage.setItem('ev_user', JSON.stringify(data));
      toast.success(t('profile.saved'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally { setSavingInfo(false); }
  };

  const changePwd = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) return toast.error(t('profile.passwords_dont_match'));
    if (pwd.new_password.length < 6) return toast.error('Min 6 chars');
    setSavingPwd(true);
    try {
      await api.post('/auth/change-password', {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success(t('profile.password_changed'));
      setPwd({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally { setSavingPwd(false); }
  };

  return (
    <div className="animate-fade-up max-w-3xl" data-testid="profile-page">
      <div className="overline text-[#002FA7]">ACCOUNT</div>
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-2">{t('profile.title')}</h1>
      <p className="text-gray-500 mt-2">{t('profile.subtitle')}</p>

      {/* Language */}
      <section className="surface p-6 mt-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center">
            <Languages className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <div>
            <div className="font-display font-bold text-lg">{t('profile.language_section')}</div>
            <div className="text-xs text-gray-500">{t('profile.language_help')}</div>
          </div>
        </div>
        <LanguageSwitcher />
      </section>

      {/* Identity */}
      <section className="surface p-6 mt-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center">
            <UserIcon className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <div>
            <div className="font-display font-bold text-lg">{t('profile.personal_info')}</div>
            <div className="text-xs text-gray-500 font-mono">ID: {user?.id?.slice(0, 8)}… · <span className="uppercase">{user?.role}</span></div>
          </div>
        </div>
        <form onSubmit={saveInfo} className="mt-5 space-y-4" data-testid="info-form">
          <div>
            <label className="overline text-gray-600 block mb-1">{t('profile.full_name')}</label>
            <input required className="input-base" value={info.full_name} onChange={(e) => setInfo({ ...info, full_name: e.target.value })} data-testid="profile-name" />
          </div>
          <div>
            <label className="overline text-gray-600 block mb-1">{t('profile.email')}</label>
            <input type="email" required className="input-base font-mono" value={info.email} onChange={(e) => setInfo({ ...info, email: e.target.value })} data-testid="profile-email" />
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={savingInfo} className="btn-primary" data-testid="save-info">
              <Save className="w-4 h-4" />
              {savingInfo ? t('app.saving') : t('profile.save_info')}
            </button>
          </div>
        </form>
      </section>

      {/* Password */}
      <section className="surface p-6 mt-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 bg-red-50 text-red-700 flex items-center justify-center">
            <Lock className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <div>
            <div className="font-display font-bold text-lg">{t('profile.change_password')}</div>
          </div>
        </div>
        <form onSubmit={changePwd} className="mt-5 space-y-4" data-testid="pwd-form">
          <div>
            <label className="overline text-gray-600 block mb-1">{t('profile.current_password')}</label>
            <input type="password" required className="input-base" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="current-pwd" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="overline text-gray-600 block mb-1">{t('profile.new_password')}</label>
              <input type="password" required minLength={6} className="input-base" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="new-pwd" />
            </div>
            <div>
              <label className="overline text-gray-600 block mb-1">{t('profile.confirm_password')}</label>
              <input type="password" required minLength={6} className="input-base" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="confirm-pwd" />
            </div>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={savingPwd} className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-2.5 font-semibold hover:bg-red-700 active:scale-95 transition-all disabled:opacity-50" data-testid="save-pwd">
              <Shield className="w-4 h-4" />
              {savingPwd ? t('app.saving') : t('profile.save_password')}
            </button>
          </div>
        </form>
      </section>

      <div className="mt-8 flex items-start gap-3 border border-blue-200 bg-blue-50 p-4 text-sm">
        <Mail className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
        <div className="text-gray-700">{user?.role === 'admin' ? 'Admin Dashboard → Users' : ''}</div>
      </div>
    </div>
  );
}
